import { ChangeDetectionStrategy, Component, computed, inject, signal, ViewEncapsulation, OnInit } from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import {
    IOnboardingReportContainer,
    IOnboardingReportAvailableItems,
    IOnboardingReportWidget,
    IOnboardingReportWidgetFilter,
    IOnboardingReportWidgetSettings,
} from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report';
import { FiOnboardingReportHelperService } from '@fi-app/fi-onboarding/fi-onboarding-report/services/fi-onboarding-report-helper.service';
import { FiOnboardingReportWidgetType } from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report-enums';
import { FiOnboardingReportDataService } from '@fi-app/fi-onboarding/fi-onboarding-report/services/fi-onboarding-report-data.service';
import { isEqual } from '@fi-app/shared/utils/is-equal';
import { Observable, Subject } from 'rxjs';
import { guid } from '@fi-app/shared/utils/guid';
import { FiItemsListFilterHelperService } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter-helper.service';
import { FiOnboardingReportService } from '@fi-app/fi-onboarding/fi-onboarding-report/services/fi-onboarding-report.service';
import { ItemsListFilterDialogSnackbarService } from '@fi-app/shared/fi-items-list/fi-items-list-filter/services/items-list-filter-dialog-snackbar.service';

@Component({
    selector: 'fi-recent-activity-widget-dialog',
    templateUrl: './recent-activity-widget-dialog.component.html',
    styleUrls: ['./recent-activity-widget-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-recent-activity-widget-dialog' },
    standalone: false,
})
export class FiRecentActivityWidgetDialogComponent
    extends FiDialogsContentBase<IOnboardingReportWidget, IOnboardingReportWidget>
    implements OnInit
{
    private fiOnboardingReportHelperService = inject(FiOnboardingReportHelperService);
    private fiOnboardingReportDataService = inject(FiOnboardingReportDataService);
    private itemsListFilterDialogSnackbarService = inject(ItemsListFilterDialogSnackbarService);
    private fiOnboardingReportService = inject(FiOnboardingReportService);

    protected reportContainer = signal<IOnboardingReportContainer>(null);
    protected availableItems = computed<IOnboardingReportAvailableItems>(() => this.reportContainer()?.AvailableItems);
    protected currentWidget = signal<IOnboardingReportWidget>(this.data);
    protected savedWidget = signal<IOnboardingReportWidget>(this.data);

    protected currentFilter = signal<IOnboardingReportWidgetFilter>(this.getInitialCurrentFilter());

    protected isResetButtonShown = computed<boolean>(() => {
        return this.getFilterCounter() > 0;
    });

    protected savedFilters = computed<IOnboardingReportWidgetFilter[]>(() => {
        return this.getNamedFilters(this.savedWidget().Settings.Filters);
    });

    protected isFilterSaveButtonShown = computed<boolean>(() => {
        return this.isResetButtonShown() && (this.isFilterChanged() || !this.currentFilter().Name?.length);
    });

    protected isFilterChanged = computed<boolean>(() => {
        return !isEqual(
            this.currentFilter(),
            this.savedWidget().Settings.Filters.find((filter) => filter.Guid === this.currentFilter().Guid),
        );
    });

    protected canUpdateFilter = computed<boolean>(() => {
        return this.isFilterChanged() && this.currentFilter().Name?.length > 0;
    });

    private _selectedFilterItemsChanged$ = new Subject<void>();
    protected get selectedFilterItemsChanged$(): Observable<void> {
        return this._selectedFilterItemsChanged$.asObservable();
    }

    public ngOnInit(): void {
        this.fiOnboardingReportDataService.getReportContainer().then((reportContainer) => {
            this.reportContainer.set({ ...reportContainer });
        });
    }

    protected onCurrentFilterChanged(currentFilter: IOnboardingReportWidgetFilter): void {
        this.currentFilter.set({ ...currentFilter });
        this.updateCurrentWidgetSettings({
            CurrentFilterGuid: currentFilter.Guid,
            Filters: this.getFiltersWithCurrentFilter(),
        });
        this._selectedFilterItemsChanged$.next();
    }

    private getInitialCurrentFilter(): IOnboardingReportWidgetFilter {
        const filter = this.fiOnboardingReportHelperService.getReportWidgetFilter(this.currentWidget());

        if (this.currentWidget().Settings.CurrentFilterGuid) {
            return filter;
        }

        const filterWithGuid = {
            ...filter,
            Guid: guid(),
        };
        this.updateCurrentWidgetSettings({
            CurrentFilterGuid: filterWithGuid.Guid,
            Filters: [...this.savedWidget().Settings.Filters, filterWithGuid],
        });

        return filterWithGuid;
    }

    private updateCurrentWidgetSettings(settings: Partial<IOnboardingReportWidgetSettings>): void {
        this.currentWidget.update((widget) => ({
            ...widget,
            Settings: {
                ...widget.Settings,
                ...settings,
            },
        }));
    }

    private getFiltersWithCurrentFilter(
        filters = this.currentWidget().Settings.Filters,
    ): IOnboardingReportWidgetFilter[] {
        const currentFilter = this.currentFilter();
        const currentFilterIndex = filters.findIndex((filter) => filter.Guid === currentFilter.Guid);

        if (currentFilterIndex === -1) {
            return [...filters, currentFilter];
        }

        return filters.map((filter) => (filter.Guid === currentFilter.Guid ? currentFilter : filter));
    }

    private getNamedFilters(filters: IOnboardingReportWidgetFilter[]): IOnboardingReportWidgetFilter[] {
        return filters.filter((filter) => filter?.Name?.length > 0);
    }

    private removeGuidOnlyFilters(filters: IOnboardingReportWidgetFilter[]): IOnboardingReportWidgetFilter[] {
        return filters.filter((filter) => {
            const filterKeys = Object.keys(filter);
            return !(filterKeys.length === 1 && filterKeys[0] === 'Guid');
        });
    }

    protected resetCurrentFilter(): void {
        this.currentFilter.set({});
        this._selectedFilterItemsChanged$.next();
        this.updateCurrentWidgetSettings({
            CurrentFilterGuid: undefined,
            Filters: this.removeGuidOnlyFilters(this.savedWidget().Settings.Filters),
        });
    }

    protected onSavedFiltersChange(savedFilters: IOnboardingReportWidgetFilter[]): void {
        const isSelectedFilterDeleted =
            savedFilters.find((filter) => filter.Guid === this.currentFilter().Guid) === undefined;
        this.updateCurrentWidgetSettings({
            CurrentFilterGuid: isSelectedFilterDeleted ? undefined : this.currentFilter().Guid,
            Filters: savedFilters,
        });
        this.saveUpdatedRecentActivityWidget();
    }

    protected onSelectFilter(filterGuid: string): void {
        const filter = this.savedWidget().Settings.Filters.find((filter) => filter.Guid === filterGuid);
        if (!filter) {
            return;
        }

        this.currentFilter.set({ ...filter });
        this.updateCurrentWidgetSettings({
            CurrentFilterGuid: filterGuid,
        });
        this._selectedFilterItemsChanged$.next();
        this.saveUpdatedRecentActivityWidget();
    }

    protected updateCurrentFilter(): void {
        this.updateCurrentWidgetSettings({
            CurrentFilterGuid: this.currentFilter().Guid,
            Filters: this.getFiltersWithCurrentFilter(),
        });
        this.saveUpdatedRecentActivityWidget();
    }

    protected saveFilter(): void {
        this.itemsListFilterDialogSnackbarService.getSaveFilterDialog('').onYesButtonClick((result: string) => {
            const filterGuid = this.currentFilter().Guid ? this.currentFilter().Guid : guid();
            this.currentFilter.update((filter) => ({
                ...filter,
                Name: result,
                Guid: filterGuid,
            }));
            this.updateCurrentWidgetSettings({
                CurrentFilterGuid: this.currentFilter().Guid,
                Filters: this.getFiltersWithCurrentFilter(),
            });
            this.saveUpdatedRecentActivityWidget();
        });
    }

    private saveUpdatedRecentActivityWidget(): void {
        this.fiOnboardingReportService.saveReportWidget(this.currentWidget()).then(() => {
            this.savedWidget.set({ ...this.currentWidget() });
            this.fiOnboardingReportDataService.updateRecentActivityWidget(this.currentWidget());
        });
    }

    protected closeDialog(): void {
        // https://chainformation.atlassian.net/browse/FI-6851?focusedCommentId=102446
        // don't update widget filter if it is not saved
        //this.addCurrentFilterToWidgetIfNotSaved();

        this.fiOnboardingReportDataService.updateRecentActivityWidget(this.currentWidget());
        this.dialogRef.close();
    }

    /*private addCurrentFilterToWidgetIfNotSaved(): void {
        if (!this.isFilterSaveButtonShown()) {
            return;
        }

        this.currentFilter.update((filter) => {
            return {
                ...filter,
                Guid: guid(),
                Name: '',
            };
        });
        this.updateCurrentWidgetSettings({
            CurrentFilterGuid: this.currentFilter().Guid,
            Filters: this.getFiltersWithCurrentFilter(this.savedWidget().Settings.Filters),
        });
    }*/

    private getFilterCounter(): number {
        return Object.values(this.currentFilter())
            .filter(Array.isArray)
            .reduce((counter, arr) => counter + arr.length, 0);
    }

    FiOnboardingReportWidgetType = FiOnboardingReportWidgetType;
}
